/**
 * Doserne Academy — mandatory training gate (web).
 * - TrainingCountdownCard: shown on Home + Daily plan before the deadline.
 * - TrainingLockedScreen: full-screen lock after the deadline for users who
 *   still have required courses left. Only "go to courses" + logout remain.
 * All copy is localised to the worker's menu language (no/ro/pl/en).
 */
import React, { useEffect, useState } from "react";
import { GraduationCap, Lock, LogOut, Clock, ArrowRight } from "lucide-react";

// Localised copy. {n} = number of remaining required courses.
const GATE_I18N = {
  no: {
    title: "Obligatorisk opplæring",
    body: "Du har {n} kurs igjen. Etter fristen sperres appen til du har fullført den obligatoriske opplæringen.",
    tid: "Tid igjen", d: "dager", h: "timer", m: "min", s: "sek",
    start: "Start opplæring",
    lockedTitle: "Fullfør opplæringen",
    lockedBody: "Interne regler: du må fullføre alle de obligatoriske kursene før du kan bruke resten av plattformen.",
    left: "kurs gjenstår", go: "Gå til kurs", logout: "Logg ut",
  },
  ro: {
    title: "Instruire obligatorie",
    body: "Mai ai {n} cursuri de terminat. După termen, accesul la aplicație va fi blocat până termini instruirea obligatorie.",
    tid: "Timp rămas", d: "zile", h: "ore", m: "min", s: "sec",
    start: "Începe instruirea",
    lockedTitle: "Finalizează cursurile",
    lockedBody: "Reguli interne: trebuie să finalizezi toate cursurile obligatorii înainte de a folosi restul platformei.",
    left: "cursuri rămase", go: "Mergi la cursuri", logout: "Deconectare",
  },
  pl: {
    title: "Obowiązkowe szkolenie",
    body: "Masz jeszcze {n} kursów. Po terminie dostęp do aplikacji zostanie zablokowany do czasu ukończenia obowiązkowego szkolenia.",
    tid: "Pozostały czas", d: "dni", h: "godz", m: "min", s: "sek",
    start: "Rozpocznij szkolenie",
    lockedTitle: "Ukończ szkolenie",
    lockedBody: "Zasady wewnętrzne: musisz ukończyć wszystkie obowiązkowe kursy, zanim skorzystasz z reszty platformy.",
    left: "kursów pozostało", go: "Przejdź do kursów", logout: "Wyloguj się",
  },
  en: {
    title: "Mandatory training",
    body: "You have {n} courses left. After the deadline the app will be locked until you finish the mandatory training.",
    tid: "Time left", d: "days", h: "hrs", m: "min", s: "sec",
    start: "Start training",
    lockedTitle: "Complete your training",
    lockedBody: "Internal rules: you must complete all mandatory courses before you can use the rest of the platform.",
    left: "courses left", go: "Go to courses", logout: "Log out",
  },
};
const tx = (lang) => GATE_I18N[lang] || GATE_I18N.no;

function useCountdown(deadline) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const target = deadline ? new Date(deadline).getTime() : 0;
  let diff = Math.max(0, target - now);
  const days = Math.floor(diff / 86400000); diff -= days * 86400000;
  const hours = Math.floor(diff / 3600000); diff -= hours * 3600000;
  const mins = Math.floor(diff / 60000); diff -= mins * 60000;
  const secs = Math.floor(diff / 1000);
  return { days, hours, mins, secs, past: target > 0 && now > target };
}

function TimeBox({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-2xl sm:text-3xl font-black tabular-nums leading-none">
        {String(value).padStart(2, "0")}
      </span>
      <span className="text-[10px] uppercase tracking-widest opacity-70 mt-1">{label}</span>
    </div>
  );
}

export function TrainingCountdownCard({ deadline, remaining, onStart, lang }) {
  const c = useCountdown(deadline);
  const L = tx(lang);
  return (
    <div
      data-testid="training-countdown-card"
      className="mb-6 rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 p-5 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-amber-500 flex items-center justify-center shrink-0 shadow">
          <GraduationCap className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-black text-amber-900">{L.title}</h3>
          <p className="text-sm text-amber-800/90 mt-0.5">{L.body.replace("{n}", remaining)}</p>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 text-amber-900">
        <Clock className="w-4 h-4 opacity-70" />
        <span className="text-[11px] uppercase tracking-widest font-bold opacity-80">{L.tid}</span>
      </div>
      <div className="mt-2 flex items-center gap-4 text-amber-900">
        <TimeBox value={c.days} label={L.d} />
        <span className="text-2xl font-black opacity-30">:</span>
        <TimeBox value={c.hours} label={L.h} />
        <span className="text-2xl font-black opacity-30">:</span>
        <TimeBox value={c.mins} label={L.m} />
        <span className="text-2xl font-black opacity-30">:</span>
        <TimeBox value={c.secs} label={L.s} />
      </div>

      <button
        type="button"
        onClick={onStart}
        data-testid="training-countdown-start"
        className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-black shadow-sm"
      >
        {L.start} <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

export function AccessLockedScreen({ accessFrom, onStart, onLogout, lang }) {
  const A = {
    no: { title: "Kontoen din er ikke aktiv ennå", body: "Du får full tilgang til plattformen fra datoen under. Frem til da kan du kun bruke kursene.", from: "Aktiv fra", go: "Gå til kurs", logout: "Logg ut" },
    ro: { title: "Contul tău nu este activ încă", body: "Vei primi acces complet la platformă din data de mai jos. Până atunci poți folosi doar cursurile.", from: "Activ din", go: "Mergi la cursuri", logout: "Deconectare" },
    pl: { title: "Twoje konto nie jest jeszcze aktywne", body: "Pełny dostęp do platformy otrzymasz od poniższej daty. Do tego czasu możesz korzystać tylko z kursów.", from: "Aktywne od", go: "Przejdź do kursów", logout: "Wyloguj się" },
    en: { title: "Your account isn't active yet", body: "You'll get full platform access from the date below. Until then you can only use the courses.", from: "Active from", go: "Go to courses", logout: "Log out" },
  };
  const L = A[lang] || A.no;
  let dateStr = accessFrom || "";
  try {
    const d = new Date((accessFrom || "").slice(0, 10));
    if (!isNaN(d.getTime())) dateStr = d.toLocaleDateString(lang === "no" ? "no-NO" : lang === "ro" ? "ro-RO" : lang === "pl" ? "pl-PL" : "en-GB", { day: "2-digit", month: "long", year: "numeric" });
  } catch { /* keep raw */ }
  return (
    <div data-testid="access-locked-screen" className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center rounded-3xl border border-slate-200 bg-white p-8 shadow-lg">
        <div className="w-16 h-16 rounded-2xl bg-slate-900 flex items-center justify-center mx-auto shadow">
          <Clock className="w-8 h-8 text-cyan-400" />
        </div>
        <h1 className="text-2xl font-black text-slate-900 mt-5">{L.title}</h1>
        <p className="text-sm text-slate-600 mt-2">{L.body}</p>
        <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-cyan-50 border border-cyan-200">
          <Clock className="w-4 h-4 text-cyan-600" />
          <span className="text-sm font-black text-cyan-800">{L.from}: {dateStr}</span>
        </div>
        <button type="button" onClick={onStart} data-testid="access-locked-start"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-black shadow-sm">
          <GraduationCap className="w-5 h-5" /> {L.go}
        </button>
        <button type="button" onClick={onLogout} data-testid="access-locked-logout"
          className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-500 text-sm font-bold hover:bg-slate-50">
          <LogOut className="w-4 h-4" /> {L.logout}
        </button>
      </div>
    </div>
  );
}

export function TrainingLockedScreen({ remaining, onStart, onLogout, lang }) {
  const L = tx(lang);
  return (
    <div
      data-testid="training-locked-screen"
      className="min-h-[70vh] flex items-center justify-center px-4"
    >
      <div className="max-w-md w-full text-center rounded-3xl border border-slate-200 bg-white p-8 shadow-lg">
        <div className="w-16 h-16 rounded-2xl bg-slate-900 flex items-center justify-center mx-auto shadow">
          <Lock className="w-8 h-8 text-amber-400" />
        </div>
        <h1 className="text-2xl font-black text-slate-900 mt-5">{L.lockedTitle}</h1>
        <p className="text-sm text-slate-600 mt-2">{L.lockedBody}</p>
        <p className="text-sm font-bold text-amber-700 mt-3">{remaining} {L.left}</p>
        <button
          type="button"
          onClick={onStart}
          data-testid="training-locked-start"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-black shadow-sm"
        >
          <GraduationCap className="w-5 h-5" /> {L.go}
        </button>
        <button
          type="button"
          onClick={onLogout}
          data-testid="training-locked-logout"
          className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-500 text-sm font-bold hover:bg-slate-50"
        >
          <LogOut className="w-4 h-4" /> {L.logout}
        </button>
      </div>
    </div>
  );
}
